/**
 * Generation Locks Section
 * Displays active pick generation processes for debugging stuck generations
 */

import { LoadingSpinner } from '@components/ui';
import { useGenerationLocks } from '@hooks/useGenerationLocks';
import { AlertTriangle } from 'lucide-react';
import type { ReactElement } from 'react';

export function GenerationLocksSection(): ReactElement {
  const {
    locks: generationLocks,
    loading: locksLoading,
    error: locksError,
    refetch: refetchLocks,
  } = useGenerationLocks();

  return (
    <div className="bg-theme-surface rounded-lg p-6 border border-theme-border space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-theme-text">Generation Locks</h2>
          <p className="text-sm text-theme-text-tertiary">
            Active pick generation processes (for debugging stuck generations)
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetchLocks()}
          disabled={locksLoading}
          className="text-sm text-theme-text-secondary hover:text-theme-text"
        >
          {locksLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {locksLoading ? (
        <div className="flex items-center justify-center py-4">
          <LoadingSpinner />
        </div>
      ) : locksError ? (
        <div className="bg-theme-danger/10 border border-theme-danger/20 rounded-lg p-4 text-theme-danger text-sm">
          {locksError}
        </div>
      ) : generationLocks ? (
        <>
          <div className="grid grid-cols-1 @3xl:grid-cols-3 gap-4">
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Active Locks</p>
              <p className="text-2xl font-bold text-theme-text">
                {generationLocks.activeLocksCount}
              </p>
            </div>
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Stale Locks (&gt;3 min)</p>
              <p
                className={`text-2xl font-bold ${generationLocks.staleLocks > 0 ? 'text-theme-danger' : 'text-theme-success'}`}
              >
                {generationLocks.staleLocks}
              </p>
            </div>
            <div className="bg-theme-bg rounded-lg p-4">
              <p className="text-sm text-theme-text-tertiary">Last Updated</p>
              <p className="text-sm font-mono text-theme-text">
                {new Date(generationLocks.timestamp).toLocaleTimeString()}
              </p>
            </div>
          </div>

          {generationLocks.locks.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-theme-text-secondary">
                Active Locks Details
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-theme-bg">
                    <tr>
                      <th className="text-left p-2 text-theme-text-secondary font-medium">
                        User ID
                      </th>
                      <th className="text-left p-2 text-theme-text-secondary font-medium">
                        Request ID
                      </th>
                      <th className="text-right p-2 text-theme-text-secondary font-medium">
                        Age (min)
                      </th>
                      <th className="text-center p-2 text-theme-text-secondary font-medium">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {generationLocks.locks.map((lock) => (
                      <tr key={lock.requestId} className="border-t border-theme-border">
                        <td className="p-2 text-theme-text font-mono text-xs">
                          {lock.userId.substring(0, 8)}...
                        </td>
                        <td className="p-2 text-theme-text font-mono text-xs">{lock.requestId}</td>
                        <td className="p-2 text-right text-theme-text">{lock.ageMinutes}</td>
                        <td className="p-2 text-center">
                          {lock.isStale ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-theme-danger/10 text-theme-danger text-xs">
                              <AlertTriangle className="w-3 h-3" />
                              Stale
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-theme-success/10 text-theme-success text-xs">
                              Active
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-theme-text-tertiary">
              No active generation locks
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
